import React, { useEffect, useMemo, useRef, useState } from "react";
import API_BASE_URL from '../../config/api';
import { useTranslation } from '../../i18n';

const API_BASE = API_BASE_URL;

export default function AthleteCoachBot({
  athleteId = null,
  athleteName: athleteNameProp = "",
  isTrainerView = false,
  onSend,
}) {
  const { t, language } = useTranslation();

  const [messages, setMessages] = useState([
    { role: "assistant", content: t('athleteDashboard.coachBot.greeting') },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [athleteName, setAthleteName] = useState(athleteNameProp || null);

  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const esRef = useRef(null);

  const scrollToEnd = () => {
    const box = boxRef.current;
    if (box) box.scrollTop = box.scrollHeight;
  };

  const autoResizeTextarea = () => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    const next = Math.min(el.scrollHeight, 120);
    el.style.height = `${Math.max(next, 38)}px`;
  };

  const inferFocus = (text) => {
    const t = (text || "").toLowerCase();
    const pmcHits = ["pmc","ctl","atl","tsb","banister","carga crónica","carga aguda","fatiga","forma","fitness","fatigue","form"].some(k => t.includes(k));
    const planHits = ["plan","semana","programa","entrenamiento","entreno","sesión","sesion","hoy","mañana","calendario"].some(k => t.includes(k));
    if (pmcHits && !planHits) return "pmc";
    if (planHits && !pmcHits) return "plan";
    return "";
  };

  useEffect(() => { autoResizeTextarea(); }, []);

  // Solo scrollea el box del bot cuando hay mensajes nuevos (no en el mount inicial)
  const prevMsgCount = useRef(1);
  useEffect(() => {
    if (messages.length > prevMsgCount.current) {
      scrollToEnd();
    }
    prevMsgCount.current = messages.length;
  }, [messages]);
  useEffect(() => autoResizeTextarea(), [input]);
  useEffect(() => {
    return () => { try { esRef.current?.close(); } catch {} esRef.current = null; };
  }, []);
  useEffect(() => {
    if (athleteNameProp && athleteNameProp !== athleteName) setAthleteName(athleteNameProp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [athleteNameProp]);

  useEffect(() => {
    let ignore = false;
    const fetchAthlete = async () => {
      if (athleteNameProp) return;
      if (!athleteId) return;
      try {
        const res = await fetch(`${API_BASE}/api/athletes/profile/${athleteId}`, { credentials: "include" });
        if (!res.ok) return;
        const data = await res.json();
        const a = data?.athlete;
        const full = [a?.firstName, a?.lastName].filter(Boolean).join(" ") || null;
        if (!ignore) setAthleteName(full);
      } catch { /* silent */ }
    };
    fetchAthlete();
    return () => { ignore = true; };
  }, [athleteId, athleteNameProp]);

  const canSend = useMemo(() => input.trim().length > 0 && !sending, [input, sending]);

  const sendInternal = async (text) => {
    try { esRef.current?.close(); } catch {}
    esRef.current = null;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    setInput("");
    setTimeout(scrollToEnd, 0);

    if (onSend) {
      try {
        const reply = await onSend(text);
        if (reply) setMessages((m) => [...m, { role: "assistant", content: String(reply) }]);
      } finally { setSending(false); scrollToEnd(); }
      return;
    }

    const url = new URL(`${API_BASE}/api/coachbot/query`);
    url.searchParams.set("q", text);
    url.searchParams.set("lang", language || "es");
    url.searchParams.set("isTrainerView", String(!!isTrainerView));
    const focus = inferFocus(text);
    if (focus) url.searchParams.set("focus", focus);
    if (athleteId) url.searchParams.set("athleteId", athleteId);
    if (athleteName) url.searchParams.set("athleteName", athleteName);

    const fallbackToPost = async () => {
      try {
        const r = await fetch(`${API_BASE}/api/coachbot/query`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ athleteId: athleteId || undefined, athleteName: athleteName || undefined, isTrainerView: !!isTrainerView, q: text, question: text, focus: focus || undefined, lang: language || "es" })
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        const answer = data?.answer || data?.message || t('athleteDashboard.coachBot.noAnswer');
        setMessages((m) => [...m, { role: "assistant", content: String(answer) }]);
      } catch {
        setMessages((m) => [...m, { role: "assistant", content: t('athleteDashboard.coachBot.connectError') }]);
      } finally { setSending(false); scrollToEnd(); }
    };

    try {
      const es = new EventSource(url.toString(), { withCredentials: true });
      esRef.current = es;
      let reply = "";
      es.onmessage = (e) => {
        const chunk = String(e.data || "");
        if (chunk === "[FIN]") {
          try { es.close(); } catch {}
          esRef.current = null;
          setSending(false);
          setMessages((msgs) => msgs.map((m) => (m.meta === "stream" ? { ...m, meta: undefined } : m)));
          return;
        }
        reply += chunk;
        setMessages((msgs) => {
          const last = msgs[msgs.length - 1];
          if (last && last.role === "assistant" && last.meta === "stream") {
            const next = [...msgs];
            next[next.length - 1] = { ...last, content: reply };
            return next;
          }
          return [...msgs, { role: "assistant", content: reply, meta: "stream" }];
        });
        scrollToEnd();
      };
      es.onerror = () => { try { es.close(); } catch {} esRef.current = null; fallbackToPost(); };
    } catch (err) {
      console.error("❌ Error conectando con CoachBot:", err);
      fallbackToPost();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    sendInternal(text);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSubmit(e); }
  };

  const quickAsk = (text) => {
    if (sending) return;
    setInput(text);
    setTimeout(() => sendInternal(text), 0);
  };

  return (
    <div className="coachbot d-flex flex-column" style={{ height: "100%", minHeight: 420 }}>
      <div className="px-3 py-2 border-bottom bg-white">
        <div className="text-muted" style={{ fontSize: 12 }}>{t('common.athlete')}:</div>
        <div className="fw-semibold" style={{ marginTop: -2 }}>
          {athleteName || (athleteId ? `ID: ${athleteId}` : "—")}
        </div>
      </div>

      <div ref={boxRef} className="coachbot-messages flex-grow-1 overflow-auto p-3" style={{ background: "#f8f9fa", minWidth: 0 }}>
        {athleteName && messages[0]?.role === "assistant" && messages[0]?.content?.startsWith("Hola") && (
          <div className="mb-2 text-muted small">
            {t('athleteDashboard.coachBot.welcomeNote', { name: athleteName })}
          </div>
        )}
        <div className="d-flex flex-wrap gap-2 mb-3">
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => quickAsk(t('athleteDashboard.coachBot.quickTsb'))}>
            {t('athleteDashboard.coachBot.quickTsbLabel')}
          </button>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => quickAsk(t('athleteDashboard.coachBot.quickWeek'))}>
            {t('athleteDashboard.coachBot.quickWeekLabel')}
          </button>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => quickAsk(t('athleteDashboard.coachBot.quickLoad'))}>
            {t('athleteDashboard.coachBot.quickLoadLabel')}
          </button>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => quickAsk(t('athleteDashboard.coachBot.quickTomorrow'))}>
            {t('athleteDashboard.coachBot.quickTomorrowLabel')}
          </button>
        </div>

        {messages.map((m, i) => {
          const isUser = m.role === "user";
          return (
            <div key={i} className={`d-flex mb-2 ${isUser ? "justify-content-end" : "justify-content-start"}`}>
              <div className={`coachbot-bubble ${isUser ? "user" : "assistant"}`} style={{
                maxWidth: "85%", padding: "10px 12px", borderRadius: 16, lineHeight: 1.35,
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)", overflowWrap: "anywhere", wordBreak: "normal", whiteSpace: "pre-wrap",
                ...(isUser
                  ? { background: "#0d6efd", color: "white", borderTopRightRadius: 4 }
                  : { background: "white", color: "#212529", borderTopLeftRadius: 4, border: "1px solid #e9ecef" }),
              }}>
                {m.content}
              </div>
            </div>
          );
        })}
      </div>

      <form onSubmit={handleSubmit} className="coachbot-input d-flex flex-wrap align-items-end gap-2 p-2 border-top bg-white" style={{ overflow: "visible" }} autoComplete="off">
        <div className="flex-grow-1" style={{ flexBasis: "260px", minWidth: 260, maxWidth: "100%" }}>
          <textarea
            ref={inputRef}
            className="form-control w-100"
            placeholder={t('athleteDashboard.coachBot.inputPlaceholder')}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            rows={1}
            style={{ boxSizing: "border-box", width: "100%", minWidth: 0, resize: "none", overflow: "hidden", minHeight: 38, maxHeight: 120, lineHeight: 1.4, whiteSpace: "pre-wrap", wordBreak: "normal" }}
          />
        </div>
        <button type="submit" className="btn btn-dark px-3 flex-shrink-0" disabled={!canSend} style={{ height: 38 }}>
          {sending ? (
            <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />{t('athleteDashboard.coachBot.sending')}</>
          ) : t('common.send')}
        </button>
      </form>

      <div className="px-3 pb-2 text-muted" style={{ fontSize: 12 }}>
        {isTrainerView ? t('athleteDashboard.coachBot.trainerView') : t('athleteDashboard.coachBot.athleteView')}
        {athleteName ? ` · ${athleteName}` : athleteId ? ` · ID: ${athleteId}` : ""}
      </div>
    </div>
  );
}
